import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePostDetail } from "../../hooks/usePostDetail";
import { api } from "../../services/api";
import Post from "../../components/Post/Post";
import CommentSection from "../../components/CommentSection/CommentSection";
import { Skeleton, SkeletonText } from "../../components/Skeleton";
import styles from "./PostDetailPage.module.scss";

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { feedItem, state, error, refetch, updateItem } = usePostDetail(postId);

  const handleLikeToggle = async (_postId: string, nextLiked: boolean) => {
    if (!feedItem || !postId) return;
    const wasLiked = feedItem.likedByMe;
    updateItem((item) => ({
      ...item,
      likedByMe: !wasLiked,
      post: { ...item.post, likeCount: item.post.likeCount + (wasLiked ? -1 : 1) },
    }));
    try {
      if (wasLiked) await api.social.unlikePost(postId);
      else await api.social.likePost(postId);
    } catch {
      updateItem((item) => ({
        ...item,
        likedByMe: wasLiked,
        post: { ...item.post, likeCount: item.post.likeCount + (wasLiked ? 1 : -1) },
      }));
    }
  };

  const handleDelete = async (_postId: string) => {
    if (!postId) return;
    try {
      await api.social.deletePost(postId);
      navigate("/");
    } catch (err) {
      console.error("Delete post error:", err);
    }
  };

  const handleEdit = async (_postId: string, newBody: string) => {
    if (!postId) return;
    try {
      const result = await api.social.editPost(postId, newBody);
      updateItem((item) => ({
        ...item,
        post: {
          ...item.post,
          body: result.body,
          updatedAt: result.updatedAt,
        },
      }));
    } catch (err) {
      console.error("Edit post error:", err);
    }
  };

  const handleCommentCountChange = (delta: number) => {
    updateItem((item) => ({
      ...item,
      post: {
        ...item.post,
        commentCount: item.post.commentCount + delta,
      },
    }));
  };

  if (state === "loading") {
    return (
      <div className={styles.postDetailPage} data-testid="post-detail-loading">
        <div className={styles.loadingState}>
          <Skeleton height="3rem" width="60%" />
          <Skeleton height="300px" width="100%" borderRadius="var(--radius-sm)" />
          <SkeletonText lines={3} lastLineWidth="40%" />
        </div>
      </div>
    );
  }

  if (state === "not-found") {
    return (
      <div className={styles.postDetailPage} data-testid="post-detail-not-found">
        <div className={styles.notFoundState}>
          <h2>Post not found</h2>
          <p>This post may have been deleted or you don't have access to it.</p>
          <Link to="/" className={styles.homeLink}>
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={styles.postDetailPage} data-testid="post-detail-error">
        <div className={styles.errorState}>
          <h2>Something went wrong</h2>
          <p>{error || "Failed to load post."}</p>
          <button className={styles.retryButton} onClick={refetch}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!feedItem || !postId) return null;

  return (
    <div className={styles.postDetailPage} data-testid="post-detail-success">
      <div className={styles.postContainer}>
        <Post
          item={feedItem}
          onLikeToggle={handleLikeToggle}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onAuthorClick={(userId) => navigate(`/profile?user=${userId}`)}
          commentsExpanded={true}
        />
        <CommentSection
          postId={postId}
          onCommentCountChange={handleCommentCountChange}
        />
      </div>
    </div>
  );
};

export default PostDetailPage;
